/**
 * Right-side icon rail — placeholder for future widgets (watchlist,
 * alerts, calendar, etc). For now it's a visual mirror of the left
 * toolbar so the chart sits in a symmetric TradingView-style frame.
 *
 * The journal icon is owner-only and toggles the TradeJournal
 * drawer (mounted in App.tsx). The other icons are still stubbed
 * pending feature work.
 */
import { useAppDispatch, useAppSelector, useCan } from "@/state/hooks";
import { CAP } from "@/api/capabilities";
import {
  setHelpOpen,
  setJournalOpen,
  setNewsOpen,
  setNotesOpen,
  setSignalOrdersOpen,
  setWeeklyBehaviourDialogOpen,
  toggleObjectsTree,
} from "@/state/slices/uiSlice";
import { toggleTheme } from "@/state/slices/appearanceSlice";
import { api } from "@/api/client";
import { NotificationsRailButton } from "@/components/Notifications/NotificationsRailButton";
import { ChatOnly } from "@/components/Chat/ChatOnly";
import { ChatRailButton } from "@/components/Chat/ChatPanel";
import { setStrategyOrdersOpen } from "@/state/slices/strategiesSlice";
import styles from "./RightSidebar.module.css";

// Sign out — arrow leaving a doorway.
const SignOutIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <path
      d="M6 2 H3 a1 1 0 0 0 -1 1 v10 a1 1 0 0 0 1 1 h3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
    <path
      d="M10 5 l3 3 l-3 3 M13 8 H6.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

async function signOut() {
  try {
    await api.signout();
  } finally {
    window.location.reload();
  }
}

// Watchlist — list of symbols you're tracking.
const WatchlistIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <rect
      x="2.5"
      y="3"
      width="11"
      height="2"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
    />
    <rect
      x="2.5"
      y="7"
      width="11"
      height="2"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
    />
    <rect
      x="2.5"
      y="11"
      width="11"
      height="2"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
    />
  </svg>
);

// Calendar (economic events).
const CalendarIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <rect
      x="2.5"
      y="3.5"
      width="11"
      height="10"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      rx="1"
    />
    <line
      x1="2.5"
      y1="6.5"
      x2="13.5"
      y2="6.5"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    <line
      x1="5"
      y1="2.5"
      x2="5"
      y2="4.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <line
      x1="11"
      y1="2.5"
      x2="11"
      y2="4.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

// Trade Journal — OPEN book at the bottom (two pages meeting at
// a raised central spine), three candlestick bars rising above
// it. The page lines slope toward the spine for the 3D-ish "book
// you can read from" silhouette.
const JournalIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    {/* --- Candles above the book --- */}
    {/* Left candle — bearish-ish (filled body) */}
    <line
      x1="4.5"
      y1="3"
      x2="4.5"
      y2="8.5"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinecap="round"
    />
    <rect
      x="3.7"
      y="4.5"
      width="1.6"
      height="2.5"
      stroke="currentColor"
      strokeWidth="0.9"
      fill="currentColor"
    />
    {/* Middle candle — tall wick, hollow body (bullish) */}
    <line
      x1="8"
      y1="1.5"
      x2="8"
      y2="8.5"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinecap="round"
    />
    <rect
      x="7.2"
      y="2.5"
      width="1.6"
      height="3.5"
      stroke="currentColor"
      strokeWidth="0.9"
      fill="none"
    />
    {/* Right candle — small filled body */}
    <line
      x1="11.5"
      y1="2.5"
      x2="11.5"
      y2="8.5"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinecap="round"
    />
    <rect
      x="10.7"
      y="4"
      width="1.6"
      height="2"
      stroke="currentColor"
      strokeWidth="0.9"
      fill="currentColor"
    />

    {/* --- Open book at the bottom --- */}
    {/* Left page — outer-bottom → outer-top → spine-top → spine-bottom */}
    <path
      d="M 2.5 14.5 L 2.5 12 L 8 11 L 8 14.5 Z"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      strokeLinejoin="round"
    />
    {/* Right page — mirror */}
    <path
      d="M 13.5 14.5 L 13.5 12 L 8 11 L 8 14.5 Z"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      strokeLinejoin="round"
    />
    {/* Left-page text lines — parallel to the page's top edge,
        sloping up toward the spine */}
    <line
      x1="3.5"
      y1="13"
      x2="7.3"
      y2="12.3"
      stroke="currentColor"
      strokeWidth="0.7"
    />
    <line
      x1="3.5"
      y1="13.8"
      x2="7.3"
      y2="13.1"
      stroke="currentColor"
      strokeWidth="0.7"
    />
    {/* Right-page text lines — mirror */}
    <line
      x1="8.7"
      y1="12.3"
      x2="12.5"
      y2="13"
      stroke="currentColor"
      strokeWidth="0.7"
    />
    <line
      x1="8.7"
      y1="13.1"
      x2="12.5"
      y2="13.8"
      stroke="currentColor"
      strokeWidth="0.7"
    />
  </svg>
);

// Notes — sticky-note with a folded corner + lines of writing.
// Visually distinct from the Trade Journal (which has a spine +
// chart) so the user can tell at a glance which surface they're
// opening.
const NotesIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <path
      d="M2.5 2.5 H11 L13.5 5 V13.5 H2.5 Z"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      strokeLinejoin="round"
    />
    <path
      d="M11 2.5 V5 H13.5"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      strokeLinejoin="round"
    />
    {/* Three lines of text */}
    <line x1="4.5" y1="7.5" x2="11.5" y2="7.5" stroke="currentColor" strokeWidth="1.1" />
    <line x1="4.5" y1="9.5" x2="11.5" y2="9.5" stroke="currentColor" strokeWidth="1.1" />
    <line x1="4.5" y1="11.5" x2="9" y2="11.5" stroke="currentColor" strokeWidth="1.1" />
  </svg>
);

// Layers / Objects Tree — stacked layers (matches the title icon in
// the Objects Tree drawer so the toggle and the panel share a glyph).
const LayersIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3 3 8l9 5 9-5-9-5z" />
    <path d="m3 16 9 5 9-5M3 12l9 5 9-5" />
  </svg>
);

// Weekly behaviour — stacked vertical bars suggesting per-day
// banded analysis.
const WeeklyBehaviourIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <rect
      x="2.5"
      y="3.5"
      width="2"
      height="9"
      fill="currentColor"
      opacity="0.6"
    />
    <rect
      x="5.5"
      y="3.5"
      width="2"
      height="9"
      fill="currentColor"
      opacity="0.35"
    />
    <rect
      x="8.5"
      y="3.5"
      width="2"
      height="9"
      fill="currentColor"
      opacity="0.6"
    />
    <rect
      x="11.5"
      y="3.5"
      width="2"
      height="9"
      fill="currentColor"
      opacity="0.35"
    />
  </svg>
);

// Help (?).
const HelpIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2" fill="none" />
    <path
      d="M6 6.5 C6 5.5 6.8 4.8 8 4.8 C9.2 4.8 10 5.5 10 6.5 C10 7.3 9.4 7.7 8.8 8.1 C8.3 8.4 8 8.7 8 9.4"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
    <circle cx="8" cy="11.2" r="0.7" fill="currentColor" />
  </svg>
);

// Sun — shown while in dark mode (click switches to light).
const SunIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <circle cx="8" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.2" fill="none" />
    <path
      d="M8 1.5 V3 M8 13 V14.5 M1.5 8 H3 M13 8 H14.5 M3.4 3.4 L4.5 4.5 M11.5 11.5 L12.6 12.6 M3.4 12.6 L4.5 11.5 M11.5 4.5 L12.6 3.4"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

// Moon — shown while in light mode (click switches to dark).
const MoonIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <path
      d="M13.5 8.6 A6 6 0 1 1 7.4 2.5 A4.6 4.6 0 0 0 13.5 8.6 Z"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      strokeLinejoin="round"
    />
  </svg>
);

// Signal chart — a candlestick with a small broadcast/reference dot, for the
// shared read-only bot_all reference pane.
const SignalChartIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <rect x="4" y="5" width="2" height="6" fill="currentColor" opacity="0.85" />
    <path d="M5 3.5 V5 M5 11 V12.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    <rect x="8.5" y="6.5" width="2" height="5" fill="currentColor" opacity="0.55" />
    <path d="M9.5 5 V6.5 M9.5 11.5 V13" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    <circle cx="12.4" cy="4" r="1.5" fill="none" stroke="currentColor" strokeWidth="1.1" />
    <circle cx="12.4" cy="4" r="0.5" fill="currentColor" />
  </svg>
);

// Strategy orders — three small bars of different heights (several bots), the
// middle one highlighted.
const StrategyOrdersIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <rect x="3" y="7" width="2" height="5" fill="currentColor" opacity="0.55" />
    <rect x="7" y="4" width="2" height="8" fill="currentColor" />
    <rect x="11" y="6" width="2" height="6" fill="currentColor" opacity="0.55" />
    <path d="M2.5 13.5 H13.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
  </svg>
);

type WidgetEntry = {
  Icon: () => JSX.Element;
  /** Feature name — shown verbatim as the hover tooltip. */
  label: string;
  /** Per-icon hue. Fed to `.btn` as the `--icon-color` CSS
   *  variable so the SVG stroke + hover background-tint stay
   *  in sync without writing six near-identical class names. */
  color: string;
};

// Stub widgets — disabled buttons. Each gets its own hue so the
// rail reads as a row of distinct destinations at a glance instead
// of a wall of identical cyan icons. Picked from a small palette
// the rest of the app already uses (no new design tokens):
//   - Watchlist → market green (#26a69a, bullish-side teal)
//   - Alerts    → amber (#f9a825, attention)
// Journal / News / Settings / Help live outside this list because
// their markup differs (owner-gating, active state).
const WIDGET_GROUPS: WidgetEntry[][] = [
  [{ Icon: WatchlistIcon, label: "Watchlist", color: "#26a69a" }],
];


// Calendar keeps its original blue (#3b82f6, scheduled / info) now that
// it's live — see the News drawer below.
const NEWS_COLOR = "#3b82f6";

export function RightSidebar() {
  const dispatch = useAppDispatch();
  const canJournal = useCan(CAP.journalRead);
  const journalOpen = useAppSelector((s) => s.ui.journalOpen);
  const wbOpen = useAppSelector((s) => s.ui.weeklyBehaviourDialogOpen);
  const notesOpen = useAppSelector((s) => s.ui.notesOpen);
  const newsOpen = useAppSelector((s) => s.ui.newsOpen);
  const signalEnabled = useAppSelector((s) => s.signal.enabled);
  const signalOnline = useAppSelector((s) => s.signal.online);
  const signalOrdersOpen = useAppSelector((s) => s.ui.signalOrdersOpen);
  const strategiesEnabled = useAppSelector((s) => s.strategies.enabled);
  const strategiesOnline = useAppSelector((s) => s.strategies.online);
  const strategyOrdersOpen = useAppSelector((s) => s.strategies.ordersOpen);
  const helpOpen = useAppSelector((s) => s.ui.helpOpen);
  const objectsTreeOpen = useAppSelector((s) => s.ui.objectsTreeOpen);
  const appTheme = useAppSelector((s) => s.appearance.theme);


  return (
    <aside className={styles.sidebar} aria-label="Widgets">
      {WIDGET_GROUPS.map((group, idx) => (
        <div key={idx}>
          {idx > 0 ? <div className={styles.groupDivider} /> : null}
          {group.map(({ Icon, label, color }) => (
            <button
              key={label}
              type="button"
              className={styles.btn}
              title={label}
              aria-label={label}
              disabled
              style={{ ["--icon-color" as string]: color }}
            >
              <Icon />
            </button>
          ))}
        </div>
      ))}
      <div className={styles.groupDivider} />
      <NotificationsRailButton />
      {/* Chat — the community room and the user's support ticket. The badge
          counts unread support replies (the public room has no read state).
          Absent while the server's chat flag is off. */}
      <ChatOnly>
        <ChatRailButton className={styles.btn} activeClassName={styles.btnActive} />
      </ChatOnly>
      <div className={styles.groupDivider} />
      <button
        type="button"
        className={`${styles.btn} ${objectsTreeOpen ? styles.btnActive : ""}`}
        title="Objects tree"
        aria-label="Objects tree"
        aria-pressed={objectsTreeOpen}
        onClick={() => dispatch(toggleObjectsTree())}
      >
        <LayersIcon />
      </button>
      <button
        type="button"
        className={`${styles.btn} ${wbOpen ? styles.btnActive : ""}`}
        title="Weekly behaviour"
        aria-label="Weekly behaviour"
        aria-pressed={wbOpen}
        onClick={() =>
          dispatch(setWeeklyBehaviourDialogOpen(!wbOpen))
        }
      >
        <WeeklyBehaviourIcon />
      </button>
      {canJournal && (
        <button
          type="button"
          className={`${styles.btn} ${journalOpen ? styles.btnActive : ""}`}
          title="Trade journal"
          aria-label="Trade journal"
          aria-pressed={journalOpen}
          onClick={() => dispatch(setJournalOpen(!journalOpen))}
        >
          <JournalIcon />
        </button>
      )}
      {/* Economic news calendar — current week from Forex Factory / FTMO.
          Public macro data, so available to every signed-in user. */}
      <button
        type="button"
        className={`${styles.btn} ${newsOpen ? styles.btnActive : ""}`}
        title="Economic calendar"
        aria-label="Economic calendar"
        aria-pressed={newsOpen}
        onClick={() => dispatch(setNewsOpen(!newsOpen))}
        style={{ ["--icon-color" as string]: NEWS_COLOR }}
      >
        <CalendarIcon />
      </button>
      {/* Notes are available to every signed-in user (each sees only
          their own); not gated on master, unlike the Trade Journal. */}
      <button
        type="button"
        className={`${styles.btn} ${notesOpen ? styles.btnActive : ""}`}
        title="Notes"
        aria-label="Notes"
        data-tour="notes"
        aria-pressed={notesOpen}
        onClick={() => dispatch(setNotesOpen(!notesOpen))}
      >
        <NotesIcon />
      </button>
      {/* Signal orders — the shared read-only bot_all account's running
          positions + pending orders as a docked list. Opens automatically with
          the Signal chart tab, and closes with it; this button is how you bring
          it back after closing it. Shown to
          every signed-in user when the feature is enabled server-side; dims
          when the source is offline. */}
      {signalEnabled && (
        <div className={styles.btnAnchor}>
          <button
            type="button"
            className={[
              styles.btn,
              signalOrdersOpen ? styles.btnActive : "",
            ]
              .filter(Boolean)
              .join(" ")}
            title={signalOnline ? "Signal orders" : "Signal orders — source offline"}
            aria-label="Signal orders"
            data-tour="signal-orders"
            aria-pressed={signalOrdersOpen}
            onClick={() => dispatch(setSignalOrdersOpen(!signalOrdersOpen))}
            style={{
              ["--icon-color" as string]: "#38bdf8",
              opacity: signalOnline ? 1 : 0.55,
            }}
          >
            <SignalChartIcon />
          </button>
        </div>
      )}
      {/* Strategy orders — the selected strategy bots' running positions and
          pending orders as a docked list (features/strategy_signals.md). Opens
          with the Strategies tab; this brings it back after closing it. */}
      {strategiesEnabled && (
        <button
          type="button"
          className={[styles.btn, strategyOrdersOpen ? styles.btnActive : ""]
            .filter(Boolean)
            .join(" ")}
          title={strategiesOnline ? "Strategy orders" : "Strategy orders — runner offline"}
          aria-label="Strategy orders"
          data-tour="strategy-orders"
          aria-pressed={strategyOrdersOpen}
          onClick={() => dispatch(setStrategyOrdersOpen(!strategyOrdersOpen))}
          style={{
            ["--icon-color" as string]: "#8b6cf6",
            opacity: strategiesOnline ? 1 : 0.55,
          }}
        >
          <StrategyOrdersIcon />
        </button>
      )}
      <div className={styles.spacer} />
      <div className={styles.groupDivider} />
      {/* Light/dark theme toggle — available to every user. */}
      <button
        type="button"
        className={styles.btn}
        title={appTheme === "light" ? "Switch to dark theme" : "Switch to light theme"}
        aria-label="Toggle light/dark theme"
        onClick={() => dispatch(toggleTheme())}
        style={{ ["--icon-color" as string]: "#f4c44c" }}
      >
        {appTheme === "light" ? <MoonIcon /> : <SunIcon />}
      </button>
      <button
        type="button"
        className={`${styles.btn} ${helpOpen ? styles.btnActive : ""}`}
        title="Help & contact"
        aria-label="Help & contact"
        aria-pressed={helpOpen}
        onClick={() => dispatch(setHelpOpen(!helpOpen))}
        style={{ ["--icon-color" as string]: "#a78bfa" }}
      >
        <HelpIcon />
      </button>
      <button
        type="button"
        className={styles.btn}
        title="Sign out"
        aria-label="Sign out"
        onClick={signOut}
        style={{ ["--icon-color" as string]: "#ef5350" }}
      >
        <SignOutIcon />
      </button>
    </aside>
  );
}
